<template>
    <div id="content">
        <div class="admin-page-header">
            <div>
                <h1 class="admin-page-title">{{ $t('catalog.title') }}</h1>
                <p class="admin-page-subtitle">
                    {{ $t('catalog.subtitle') }}
                </p>
            </div>
        </div>

        <div v-if="loading" class="catalog-empty">{{ $t('catalog.loading') }}</div>

        <div v-else class="catalog-grid">
            <!-- Sections column -->
            <aside class="catalog-pane catalog-pane-sections">
                <div class="catalog-pane-head">
                    <h2 class="catalog-pane-title">{{ $t('catalog.sections.title') }}</h2>
                    <button class="btn-purple-sm" type="button" @click="openNewSection">
                        <i class="bi bi-plus-lg"></i>
                        <span>{{ $t('catalog.sections.addButton') }}</span>
                    </button>
                </div>

                <ul class="catalog-section-list">
                    <li
                        v-for="(s, i) in sections"
                        :key="s.id"
                        :class="['catalog-section-item', { active: selectedId === s.id, hidden: s.is_hidden }]"
                        @click="selectSection(s.id)"
                    >
                        <span class="catalog-section-icon" :title="s.icon_slug">
                            <i :class="iconClassFor(s.icon_slug)"></i>
                        </span>
                        <span class="catalog-section-name">{{ s.label }}</span>
                        <span v-if="s.is_uploads" class="catalog-section-badge">UP</span>
                        <span v-if="s.is_hidden" class="catalog-section-badge danger">{{ $t('catalog.sections.hiddenBadge') }}</span>
                        <span class="catalog-section-actions" @click.stop>
                            <button class="icon-btn" :title="$t('catalog.actions.up')"  :disabled="i === 0"  @click="moveSection(s, -1)"><i class="bi bi-chevron-up"></i></button>
                            <button class="icon-btn" :title="$t('catalog.actions.down')" :disabled="i === sections.length - 1" @click="moveSection(s, 1)"><i class="bi bi-chevron-down"></i></button>
                            <button class="icon-btn" :title="$t('catalog.actions.edit')" @click="openEditSection(s)"><i class="bi bi-pencil"></i></button>
                            <button class="icon-btn" :title="s.is_hidden ? $t('catalog.actions.show') : $t('catalog.actions.hide')" @click="toggleSection(s)"><i :class="s.is_hidden ? 'bi bi-eye-slash' : 'bi bi-eye'"></i></button>
                            <button class="icon-btn danger" :title="$t('catalog.actions.delete')" @click="deleteSection(s)"><i class="bi bi-trash"></i></button>
                        </span>
                    </li>
                </ul>
            </aside>

            <!-- Widgets column -->
            <section class="catalog-pane catalog-pane-widgets">
                <div class="catalog-pane-head" v-if="selected">
                    <div>
                        <h2 class="catalog-pane-title">{{ $t('catalog.widgets.titleWithSection', { label: selected.label }) }}</h2>
                        <p class="catalog-pane-sub" v-if="selected.is_uploads">
                            {{ $t('catalog.widgets.uploadsSectionNote') }}
                        </p>
                    </div>
                    <div v-if="!selected.is_uploads" class="catalog-actions">
                        <button class="btn-ghost-sm" type="button" @click="openNewLinkWidget" :title="$t('catalog.widgets.linkTemplateTitle')">
                            <i class="bi bi-link-45deg"></i>
                            <span>{{ $t('catalog.widgets.linkButton') }}</span>
                        </button>
                        <button class="btn-purple-sm" type="button" @click="openNewWidget">
                            <i class="bi bi-plus-lg"></i>
                            <span>{{ $t('catalog.widgets.addButton') }}</span>
                        </button>
                    </div>
                </div>

                <div v-if="!selected" class="catalog-empty">{{ $t('catalog.widgets.selectSectionPrompt') }}</div>
                <div v-else-if="selected.is_uploads" class="catalog-empty">
                    {{ $t('catalog.widgets.uploadsSectionManaged') }}
                </div>
                <div v-else-if="!sectionWidgets.length" class="catalog-empty">
                    {{ $t('catalog.widgets.emptyState') }}
                </div>
                <ul v-else class="catalog-widget-list">
                    <li v-for="(w, i) in sectionWidgets" :key="w.id" :class="['catalog-widget-item', { hidden: w.is_hidden }]">
                        <span class="catalog-widget-icon">
                            <img :src="displayPicture(w.picture)" :alt="w.title" @error="onIconError($event)" />
                        </span>
                        <div class="catalog-widget-main">
                            <div class="catalog-widget-title">{{ w.title }}</div>
                            <div class="catalog-widget-meta">
                                <code>{{ w.widget_type }}</code>
                                <span v-if="w.link" class="catalog-widget-link" :title="w.link">
                                    <i class="bi bi-link-45deg"></i> {{ shortLink(w.link) }}
                                </span>
                            </div>
                        </div>
                        <span class="catalog-widget-actions">
                            <button class="icon-btn" :title="$t('catalog.actions.up')" :disabled="i === 0" @click="moveWidget(w, -1)"><i class="bi bi-chevron-up"></i></button>
                            <button class="icon-btn" :title="$t('catalog.actions.down')" :disabled="i === sectionWidgets.length - 1" @click="moveWidget(w, 1)"><i class="bi bi-chevron-down"></i></button>
                            <button class="icon-btn" :title="$t('catalog.actions.edit')" @click="openEditWidget(w)"><i class="bi bi-pencil"></i></button>
                            <button class="icon-btn" :title="w.is_hidden ? $t('catalog.actions.show') : $t('catalog.actions.hide')" @click="toggleWidget(w)"><i :class="w.is_hidden ? 'bi bi-eye-slash' : 'bi bi-eye'"></i></button>
                            <button class="icon-btn danger" :title="$t('catalog.actions.delete')" @click="deleteWidget(w)"><i class="bi bi-trash"></i></button>
                        </span>
                    </li>
                </ul>
            </section>
        </div>

        <!-- Section editor modal -->
        <div v-if="sectionForm.open" class="modal-overlay" @mousedown.self="closeSectionForm">
            <div class="modal-card">
                <div class="modal-head">
                    <h3>{{ sectionForm.id ? $t('catalog.sectionForm.editTitle') : $t('catalog.sectionForm.createTitle') }}</h3>
                    <button class="icon-btn" @click="closeSectionForm"><i class="bi bi-x-lg"></i></button>
                </div>
                <div class="modal-body">
                    <label class="form-row">
                        <span>{{ $t('catalog.sectionForm.keyLabel') }}</span>
                        <input v-model="sectionForm.key" :disabled="!!sectionForm.id" :placeholder="$t('catalog.sectionForm.keyPlaceholder')" />
                    </label>
                    <label class="form-row">
                        <span>{{ $t('catalog.sectionForm.nameLabel') }}</span>
                        <input v-model="sectionForm.label" :placeholder="$t('catalog.sectionForm.namePlaceholder')" />
                    </label>
                    <div class="form-row">
                        <span>{{ $t('catalog.sectionForm.iconLabel') }}</span>
                        <div class="icon-grid">
                            <button
                                v-for="key in iconKeys"
                                :key="key"
                                type="button"
                                :class="['icon-tile', { active: sectionForm.icon_slug === key }]"
                                @click="sectionForm.icon_slug = key"
                            >
                                <i :class="iconClassFor(key)"></i>
                                <span>{{ key }}</span>
                            </button>
                        </div>
                    </div>
                    <label class="form-row checkbox">
                        <input type="checkbox" v-model="sectionForm.is_uploads" :disabled="!!sectionForm.id" />
                        <span>{{ $t('catalog.sectionForm.uploadsCheckboxLabel') }}</span>
                    </label>
                </div>
                <div class="modal-foot">
                    <button class="btn-ghost" @click="closeSectionForm">{{ $t('catalog.cancel') }}</button>
                    <button class="btn-purple" @click="saveSection" :disabled="sectionFormSaving">
                        {{ sectionForm.id ? $t('catalog.save') : $t('catalog.create') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Widget editor modal -->
        <div v-if="widgetForm.open" class="modal-overlay" @mousedown.self="closeWidgetForm">
            <div class="modal-card">
                <div class="modal-head">
                    <h3>{{ widgetForm.id ? $t('catalog.widgetForm.editTitle') : $t('catalog.widgetForm.createTitle') }}</h3>
                    <button class="icon-btn" @click="closeWidgetForm"><i class="bi bi-x-lg"></i></button>
                </div>
                <div class="modal-body">
                    <!-- Live preview at top so the admin sees what users will see -->
                    <div class="widget-preview">
                        <span class="widget-preview-icon">
                            <img
                                v-if="widgetForm.picture"
                                :src="displayPicture(widgetForm.picture)"
                                :alt="widgetForm.title"
                                @error="onIconError($event)"
                            />
                            <i v-else class="bi bi-image"></i>
                        </span>
                        <div class="widget-preview-main">
                            <div class="widget-preview-title">{{ widgetForm.title || $t('catalog.widgetForm.defaultTitlePreview') }}</div>
                            <div class="widget-preview-meta">
                                <code>{{ widgetForm.widget_type || $t('catalog.widgetForm.typePreviewFallback') }}</code>
                                <span v-if="widgetForm.link" :title="widgetForm.link">
                                    <i class="bi bi-link-45deg"></i> {{ shortLink(widgetForm.link) }}
                                </span>
                            </div>
                        </div>
                        <span v-if="widgetForm.widget_type === 'link'" class="widget-preview-badge">{{ $t('catalog.widgetForm.linkTemplateBadge') }}</span>
                    </div>

                    <label class="form-row">
                        <span>{{ $t('catalog.widgetForm.typeLabel') }}</span>
                        <select v-model="widgetForm.widget_type" :disabled="!!widgetForm.id" @change="onTypeChange">
                            <option disabled value="">{{ $t('catalog.widgetForm.typePlaceholder') }}</option>
                            <option v-for="t in widgetTypes" :key="t.type" :value="t.type">
                                {{ t.type }} — {{ t.name }}{{ t.enabled ? '' : ' ' + $t('catalog.widgetForm.hiddenInWizardSuffix') }}
                            </option>
                        </select>
                        <small v-if="widgetForm.widget_type === 'link'" class="form-hint">
                            {{ $t('catalog.widgetForm.linkTypeHint') }}
                        </small>
                    </label>
                    <label class="form-row">
                        <span>{{ $t('catalog.widgetForm.titleLabel') }}</span>
                        <input v-model="widgetForm.title" :placeholder="titlePlaceholder" />
                    </label>
                    <label class="form-row">
                        <span>{{ $t('catalog.widgetForm.iconUrlLabel') }}</span>
                        <input v-model="widgetForm.picture" :placeholder="picturePlaceholder" />
                        <small class="form-hint">
                            {{ $t('catalog.widgetForm.iconUrlHintPrefix') }} <code>../images/widget/menu.svg</code> {{ $t('catalog.widgetForm.iconUrlHintSuffix') }}
                        </small>
                    </label>
                    <div class="form-row">
                        <span>{{ $t('catalog.widgetForm.uploadAltLabel') }}</span>
                        <button type="button" class="btn-ghost" :disabled="widgetIconUploading" @click="triggerWidgetIconUpload">
                            <i class="bi bi-crop"></i>
                            {{ widgetIconUploading ? $t('catalog.loading') : $t('catalog.widgetForm.uploadCropButton') }}
                        </button>
                        <input
                            ref="widgetIconInput"
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                            style="display:none"
                            @change="onWidgetIconFile"
                        />
                        <small class="form-hint">{{ $t('catalog.widgetForm.cropHint') }}</small>
                    </div>
                    <label class="form-row">
                        <span>{{ $t('catalog.widgetForm.linkLabel') }}</span>
                        <input v-model="widgetForm.link" :placeholder="linkPlaceholder" />
                    </label>
                </div>
                <div class="modal-foot">
                    <button class="btn-ghost" @click="closeWidgetForm">{{ $t('catalog.cancel') }}</button>
                    <button class="btn-purple" @click="saveWidget" :disabled="widgetFormSaving">
                        {{ widgetForm.id ? $t('catalog.save') : $t('catalog.add') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const ICON_KEYS = ['grid', 'settings', 'share', 'chat', 'card', 'dots', 'image']
const ICON_CLASS = {
    grid: 'bi bi-grid-3x3-gap',
    settings: 'bi bi-gear',
    share: 'bi bi-share',
    chat: 'bi bi-chat-dots',
    card: 'bi bi-credit-card',
    dots: 'bi bi-three-dots',
    image: 'bi bi-image',
}

export default {
    data: () => ({
        loading: true,
        sections: [],
        widgets: [],
        widgetTypes: [],
        selectedId: null,
        sectionForm: {
            open: false,
            id: null,
            key: '',
            label: '',
            icon_slug: 'grid',
            is_uploads: false,
        },
        sectionFormSaving: false,
        widgetForm: {
            open: false,
            id: null,
            widget_type: '',
            title: '',
            picture: '',
            link: '',
        },
        widgetFormSaving: false,
        widgetIconUploading: false,
        iconKeys: ICON_KEYS,
    }),
    computed: {
        selected() {
            return this.sections.find(s => s.id === this.selectedId) || null
        },
        sectionWidgets() {
            if (!this.selectedId) return []
            return this.widgets
                .filter(w => w.section_id === this.selectedId)
                .sort((a, b) => a.position - b.position)
        },
        titlePlaceholder() {
            if (this.widgetForm.widget_type === 'link') return 'Threads / Discord / Pinterest …'
            const t = this.widgetTypes.find(x => x.type === this.widgetForm.widget_type)
            return t ? t.name : this.$t('catalog.widgetForm.titlePlaceholderFallback')
        },
        picturePlaceholder() {
            if (this.widgetForm.widget_type === 'link') return 'https://…/icon.png'
            return '../images/widget/menu.svg'
        },
        linkPlaceholder() {
            if (this.widgetForm.widget_type === 'link') return 'https://example.com/your-page'
            return 'https://mvoter.com/interview/'
        },
    },
    async mounted() {
        this.$emit('setPageTitle', this.$t('catalog.title'))
        await Promise.all([this.reload(), this.loadWidgetTypes()])
    },
    methods: {
        iconClassFor(slug) { return ICON_CLASS[slug] || ICON_CLASS.grid },
        shortLink(l) {
            if (!l) return ''
            const trimmed = l.replace(/^https?:\/\//, '')
            return trimmed.length > 32 ? trimmed.slice(0, 32) + '…' : trimmed
        },
        // Widget pictures are stored as cabinet-relative paths
        // (e.g. '../images/widget/menu.svg'). They render fine on cabinet but
        // 404 on billing's host — so for the admin preview we rewrite to an
        // absolute cabinet URL. Storage is untouched.
        displayPicture(p) {
            if (!p) return ''
            if (/^https?:\/\//.test(p)) return p
            if (p.startsWith('data:')) return p
            const cleaned = p.replace(/^(\.\.\/)+/, '/')
            const path = cleaned.startsWith('/') ? cleaned : '/' + cleaned
            return 'https://cabinet.superqr.com' + path
        },
        onIconError(e) { e.target.style.opacity = 0.25 },

        triggerWidgetIconUpload() {
            if (this.$refs.widgetIconInput) this.$refs.widgetIconInput.click()
        },
        // Upload a custom widget icon: billing crops it to a 256×256 square
        // (sharp) and we store a stable redirect URL as the picture, so long /
        // oversized images can never break the layout.
        async onWidgetIconFile(e) {
            const file = e.target.files && e.target.files[0]
            if (e.target) e.target.value = ''
            if (!file) return
            if (!/^image\/(png|jpe?g|gif|webp|svg\+xml)$/i.test(file.type)) {
                this.$notify({ text: this.$t('catalog.widgetForm.iconTypeError'), type: 'error' })
                return
            }
            this.widgetIconUploading = true
            try {
                const fd = new FormData()
                fd.append('icon', file)
                const res = await fetch(process.env.VUE_APP_API_URL + '/spheres/upload-icon?prefix=catalog-widget-icons', {
                    method: 'POST',
                    credentials: 'include',
                    body: fd,
                })
                const json = await res.json()
                if (json.result === 'SUCCESS' && json.data && json.data.icon_url) {
                    this.widgetForm.picture = process.env.VUE_APP_API_URL + '/catalog/widget-icon/' + json.data.icon_url
                    this.$notify({ text: this.$t('catalog.widgetForm.iconUploadedSuccess'), type: 'success' })
                } else {
                    this.$notify({ text: (json && json.msg) || this.$t('catalog.loadFailed'), type: 'error' })
                }
            } catch (err) {
                this.$notify({ text: err.message || this.$t('catalog.uploadErrorGeneric'), type: 'error' })
            } finally {
                this.widgetIconUploading = false
            }
        },

        async reload() {
            this.loading = true
            const r = await this.$api.getCatalogAdmin()
            if (r.result === 'SUCCESS') {
                this.sections = r.data.sections
                this.widgets = r.data.widgets
                if (!this.selectedId && this.sections.length > 0) {
                    const firstNonAll = this.sections.find(s => s.key !== 'all') || this.sections[0]
                    this.selectedId = firstNonAll.id
                }
            } else {
                this.toast(r.msg || this.$t('catalog.loadFailed'), 'error')
            }
            this.loading = false
        },
        async loadWidgetTypes() {
            // Admin needs every type — including disabled ones — so the catalog
            // can host widgets the wizard hides (e.g. legacy numeric templates).
            const r = await fetch(
                process.env.VUE_APP_API_URL + '/widget-types?include_disabled=1',
                { credentials: 'include' },
            ).then(x => x.json()).catch(() => null)
            this.widgetTypes = (r && r.result === 'SUCCESS' && Array.isArray(r.data)) ? r.data : []
        },

        selectSection(id) { this.selectedId = id },

        // region Section CRUD
        openNewSection() {
            this.sectionForm = { open: true, id: null, key: '', label: '', icon_slug: 'grid', is_uploads: false }
        },
        openEditSection(s) {
            this.sectionForm = {
                open: true, id: s.id, key: s.key, label: s.label, icon_slug: s.icon_slug, is_uploads: s.is_uploads,
            }
        },
        closeSectionForm() { this.sectionForm.open = false },

        async saveSection() {
            this.sectionFormSaving = true
            try {
                let r
                if (this.sectionForm.id) {
                    r = await this.$api.patchCatalogSection(this.sectionForm.id, {
                        label: this.sectionForm.label,
                        icon_slug: this.sectionForm.icon_slug,
                    })
                } else {
                    r = await this.$api.createCatalogSection({
                        key: this.sectionForm.key,
                        label: this.sectionForm.label,
                        icon_slug: this.sectionForm.icon_slug,
                        is_uploads: this.sectionForm.is_uploads,
                        position: this.sections.length,
                    })
                }
                if (r.result === 'SUCCESS') {
                    this.toast(this.$t('catalog.sectionForm.savedToast'))
                    this.closeSectionForm()
                    await this.reload()
                } else {
                    this.toast(r.msg || this.$t('catalog.genericError'), 'error')
                }
            } finally {
                this.sectionFormSaving = false
            }
        },
        async toggleSection(s) {
            const r = await this.$api.patchCatalogSection(s.id, { is_hidden: !s.is_hidden })
            if (r.result === 'SUCCESS') await this.reload()
            else this.toast(r.msg || this.$t('catalog.genericError'), 'error')
        },
        async deleteSection(s) {
            if (!confirm(this.$t('catalog.sections.deleteConfirm', { label: s.label }))) return
            const r = await this.$api.deleteCatalogSection(s.id)
            if (r.result === 'SUCCESS') {
                if (this.selectedId === s.id) this.selectedId = null
                await this.reload()
            } else {
                this.toast(r.msg || this.$t('catalog.genericError'), 'error')
            }
        },
        async moveSection(s, dir) {
            const idx = this.sections.findIndex(x => x.id === s.id)
            const swap = this.sections[idx + dir]
            if (!swap) return
            await Promise.all([
                this.$api.patchCatalogSection(s.id,    { position: swap.position }),
                this.$api.patchCatalogSection(swap.id, { position: s.position }),
            ])
            await this.reload()
        },
        // endregion

        // region Widget CRUD
        openNewWidget() {
            this.widgetForm = {
                open: true, id: null, widget_type: '', title: '', picture: '', link: '',
            }
        },
        openNewLinkWidget() {
            // Pre-fills the form for a generic link widget so admin can
            // ship a new social/messenger/link tile in seconds.
            this.widgetForm = {
                open: true, id: null, widget_type: 'link', title: '', picture: '', link: '',
            }
        },
        openEditWidget(w) {
            this.widgetForm = {
                open: true, id: w.id, widget_type: w.widget_type, title: w.title,
                picture: w.picture, link: w.link || '',
            }
        },
        // Auto-fill title from widget_types.name when admin picks a type and
        // hasn't typed a title yet. Saves a click for every common widget.
        onTypeChange() {
            if (!this.widgetForm.title) {
                const t = this.widgetTypes.find(x => x.type === this.widgetForm.widget_type)
                if (t && t.name) this.widgetForm.title = t.name
            }
        },
        closeWidgetForm() { this.widgetForm.open = false },

        async saveWidget() {
            this.widgetFormSaving = true
            try {
                let r
                if (this.widgetForm.id) {
                    r = await this.$api.patchCatalogWidget(this.widgetForm.id, {
                        title: this.widgetForm.title,
                        picture: this.widgetForm.picture,
                        link: this.widgetForm.link,
                    })
                } else {
                    r = await this.$api.createCatalogWidget({
                        widget_type: this.widgetForm.widget_type,
                        section_id: this.selectedId,
                        title: this.widgetForm.title,
                        picture: this.widgetForm.picture,
                        link: this.widgetForm.link,
                        position: this.sectionWidgets.length,
                    })
                }
                if (r.result === 'SUCCESS') {
                    this.toast(this.$t('catalog.widgetForm.savedToast'))
                    this.closeWidgetForm()
                    await this.reload()
                } else {
                    this.toast(r.msg || this.$t('catalog.genericError'), 'error')
                }
            } finally {
                this.widgetFormSaving = false
            }
        },
        async toggleWidget(w) {
            const r = await this.$api.patchCatalogWidget(w.id, { is_hidden: !w.is_hidden })
            if (r.result === 'SUCCESS') await this.reload()
            else this.toast(r.msg || this.$t('catalog.genericError'), 'error')
        },
        async deleteWidget(w) {
            if (!confirm(this.$t('catalog.widgets.deleteConfirm', { title: w.title }))) return
            const r = await this.$api.deleteCatalogWidget(w.id)
            if (r.result === 'SUCCESS') await this.reload()
            else this.toast(r.msg || this.$t('catalog.genericError'), 'error')
        },
        async moveWidget(w, dir) {
            const list = this.sectionWidgets
            const idx = list.findIndex(x => x.id === w.id)
            const swap = list[idx + dir]
            if (!swap) return
            await Promise.all([
                this.$api.patchCatalogWidget(w.id,    { position: swap.position }),
                this.$api.patchCatalogWidget(swap.id, { position: w.position }),
            ])
            await this.reload()
        },
        // endregion

        toast(text, type = 'success') {
            this.$notify({ text, type })
        },
    },
}
</script>

<style scoped>
.catalog-grid {
    display: grid;
    grid-template-columns: 320px 1fr;
    gap: 18px;
    align-items: start;
}

.catalog-pane {
    background: #fff;
    border: 1px solid #ECEAF4;
    border-radius: 14px;
    padding: 14px 12px;
    box-shadow: 0 1px 2px rgba(15, 14, 26, 0.03);
}

.catalog-pane-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 0 4px 10px;
    border-bottom: 1px solid #F0EEF8;
    margin-bottom: 8px;
}

.catalog-pane-title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #0F0E1A;
}

.catalog-pane-sub {
    margin: 4px 0 0;
    font-size: 12px;
    color: #6E6C80;
    line-height: 1.4;
    max-width: 460px;
}

.catalog-section-list,
.catalog-widget-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.catalog-section-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 9px;
    cursor: pointer;
    transition: background 0.15s ease;
    border: 1px solid transparent;
}
.catalog-section-item:hover {
    background: #FAFAFD;
    border-color: #ECEAF4;
}
.catalog-section-item.active {
    background: #ECE7FE;
    border-color: #5B3FF3;
}
.catalog-section-item.hidden {
    opacity: 0.55;
}

.catalog-section-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #fff;
    border: 1px solid #ECEAF4;
    color: #5B3FF3;
}

.catalog-section-name {
    flex: 1;
    font-weight: 500;
    color: #0F0E1A;
}

.catalog-section-badge {
    font-size: 10px;
    font-weight: 700;
    color: #5B3FF3;
    background: #fff;
    border: 1px solid #D9D2F4;
    border-radius: 5px;
    padding: 2px 5px;
}
.catalog-section-badge.danger {
    color: #B42318;
    border-color: #FECDCA;
}

.catalog-section-actions {
    display: inline-flex;
    gap: 2px;
    opacity: 0;
    transition: opacity 0.15s ease;
}
.catalog-section-item:hover .catalog-section-actions,
.catalog-section-item.active .catalog-section-actions {
    opacity: 1;
}

.catalog-widget-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border: 1px solid #ECEAF4;
    border-radius: 9px;
    background: #fff;
    transition: background 0.15s ease, border-color 0.15s ease;
}
.catalog-widget-item:hover {
    background: #FAFAFD;
    border-color: #D9D2F4;
}
.catalog-widget-item.hidden { opacity: 0.55; }

.catalog-widget-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #FAFAFD;
    border: 1px solid #ECEAF4;
    display: grid;
    place-items: center;
    flex-shrink: 0;
    overflow: hidden;
}
.catalog-widget-icon img {
    max-width: 24px;
    max-height: 24px;
    object-fit: contain;
}

.catalog-widget-main {
    flex: 1;
    min-width: 0;
}
.catalog-widget-title {
    font-size: 13.5px;
    font-weight: 500;
    color: #0F0E1A;
    line-height: 1.3;
}
.catalog-widget-meta {
    font-size: 11.5px;
    color: #6E6C80;
    margin-top: 2px;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}
.catalog-widget-meta code {
    background: #F4F2FA;
    color: #5B3FF3;
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 11px;
}
.catalog-widget-link {
    color: #6E6C80;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 280px;
}

.catalog-widget-actions {
    display: inline-flex;
    gap: 2px;
}

.icon-btn {
    width: 26px;
    height: 26px;
    border: 0;
    background: transparent;
    border-radius: 6px;
    color: #6E6C80;
    cursor: pointer;
    transition: background 0.12s ease, color 0.12s ease;
}
.icon-btn:hover { background: #F4F2FA; color: #0F0E1A; }
.icon-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.icon-btn.danger:hover { background: #FEF3F2; color: #B42318; }

.catalog-empty {
    text-align: center;
    padding: 36px 12px;
    color: #6E6C80;
    font-size: 13px;
}

/* Modal */
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 14, 26, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1100;
}
.modal-card {
    width: min(520px, calc(100vw - 32px));
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 24px 56px -12px rgba(15, 14, 26, 0.32);
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 64px);
}
.modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px;
    border-bottom: 1px solid #F0EEF8;
}
.modal-head h3 {
    margin: 0;
    font-size: 15px;
    color: #0F0E1A;
}
.modal-body {
    padding: 16px 18px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.modal-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 18px;
    border-top: 1px solid #F0EEF8;
}

.form-row {
    display: flex;
    flex-direction: column;
    gap: 5px;
    font-size: 13px;
    color: #2E2C3E;
}
.form-row span {
    font-weight: 500;
    color: #4A4A5C;
    font-size: 12px;
}
.form-row input,
.form-row select {
    border: 1px solid #ECEAF4;
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 13px;
    color: #0F0E1A;
    background: #fff;
    outline: 0;
    font-family: inherit;
}
.form-row input:focus,
.form-row select:focus {
    border-color: #5B3FF3;
    box-shadow: 0 0 0 3px rgba(91, 63, 243, 0.1);
}
.form-row.checkbox {
    flex-direction: row;
    align-items: center;
    gap: 8px;
}
.form-row.checkbox input { width: auto; }
.form-row.checkbox span { font-weight: 400; }

.icon-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
}
.icon-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 10px 4px 6px;
    border: 1px solid #ECEAF4;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.12s ease, background 0.12s ease;
    color: #4A4A5C;
}
.icon-tile i { font-size: 16px; }
.icon-tile span { font-size: 10px; color: #8E8E9E; }
.icon-tile:hover { border-color: #D9D2F4; background: #FAFAFD; }
.icon-tile.active { border-color: #5B3FF3; background: #ECE7FE; color: #5B3FF3; }
.icon-tile.active span { color: #5B3FF3; }

.btn-purple-sm {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #5B3FF3;
    color: #fff;
    border: 0;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.12s ease;
}
.btn-purple-sm:hover { background: #4D34D4; }

.btn-ghost {
    background: transparent;
    color: #2E2C3E;
    border: 0;
    padding: 8px 14px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
}
.btn-ghost:hover { background: #F4F2FA; }

.btn-ghost-sm {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: transparent;
    color: #5B3FF3;
    border: 1px solid #D9D2F4;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.12s ease, border-color 0.12s ease;
}
.btn-ghost-sm:hover { background: #F4F2FA; border-color: #5B3FF3; }

.catalog-actions {
    display: inline-flex;
    gap: 6px;
    align-items: center;
}

.form-hint {
    font-size: 11px;
    color: #8E8E9E;
    line-height: 1.4;
}
.form-hint code {
    background: #F4F2FA;
    color: #5B3FF3;
    padding: 1px 4px;
    border-radius: 4px;
    font-size: 10.5px;
}

/* Live preview at top of widget form */
.widget-preview {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 1px dashed #D9D2F4;
    border-radius: 10px;
    background: linear-gradient(180deg, #FAFAFD 0%, #FFFFFF 100%);
    position: relative;
}
.widget-preview-icon {
    width: 38px;
    height: 38px;
    border-radius: 9px;
    background: #fff;
    border: 1px solid #ECEAF4;
    display: grid;
    place-items: center;
    color: #A8A6B8;
    flex-shrink: 0;
    overflow: hidden;
}
.widget-preview-icon img { max-width: 24px; max-height: 24px; object-fit: contain; }
.widget-preview-icon i { font-size: 16px; }
.widget-preview-main { flex: 1; min-width: 0; }
.widget-preview-title {
    font-size: 13.5px;
    font-weight: 600;
    color: #0F0E1A;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.widget-preview-meta {
    font-size: 11px;
    color: #6E6C80;
    margin-top: 2px;
    display: flex;
    gap: 8px;
    align-items: center;
}
.widget-preview-meta code {
    background: #F4F2FA;
    color: #5B3FF3;
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 10.5px;
}
.widget-preview-badge {
    position: absolute;
    top: -7px;
    right: 10px;
    font-size: 9.5px;
    font-weight: 700;
    background: #5B3FF3;
    color: #fff;
    padding: 2px 7px;
    border-radius: 5px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}
</style>
